import React from 'react';
import {
  Package, Receipt, TrendingUp, Truck, ArrowUpRight, ArrowDownRight,
  Clock, CheckCircle, AlertTriangle, RefreshCw
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';

const stats = [
  {
    label: 'Total Manifests',
    value: '247',
    change: '+12%',
    trend: 'up',
    icon: Package,
    color: 'blue'
  },
  {
    label: 'Pending Invoices',
    value: '34',
    change: '-8%',
    trend: 'down',
    icon: Receipt,
    color: 'amber'
  },
  {
    label: 'Revenue (MTD)',
    value: '$1.2M',
    change: '+23%',
    trend: 'up',
    icon: TrendingUp,
    color: 'lime'
  },
  {
    label: 'Active Couriers',
    value: '18',
    change: '+2',
    trend: 'up',
    icon: Truck,
    color: 'cyan'
  }
];

const revenueData = [
  { date: 'Jan 1', revenue: 45000, cost: 38000 },
  { date: 'Jan 8', revenue: 52000, cost: 41000 },
  { date: 'Jan 15', revenue: 48000, cost: 39000 },
  { date: 'Jan 22', revenue: 61000, cost: 45000 },
  { date: 'Jan 29', revenue: 55000, cost: 42000 },
  { date: 'Feb 5', revenue: 67000, cost: 48000 },
  { date: 'Feb 12', revenue: 72000, cost: 51000 },
];

const courierData = [
  { name: 'FastShip', bags: 45, singles: 120 },
  { name: 'SpeedyCourier', bags: 32, singles: 89 },
  { name: 'IslandExpress', bags: 28, singles: 65 },
  { name: 'KP Direct', bags: 0, singles: 156 },
];

const recentActivity = [
  { id: 1, type: 'manifest', message: 'Manifest MAN-2026-02-28-001 synced from Tasoko', time: '2 min ago', status: 'success' },
  { id: 2, type: 'invoice', message: 'Invoice #INV-4521 sent to FastShip Jamaica', time: '15 min ago', status: 'success' },
  { id: 3, type: 'payment', message: 'Payment received from SpeedyCourier ($12,450)', time: '1 hour ago', status: 'success' },
  { id: 4, type: 'alert', message: 'KPL Mix bag KPL-MIX-2026-045 requires splitting', time: '2 hours ago', status: 'warning' },
  { id: 5, type: 'sync', message: 'Tasoko sync completed - 23 new packages', time: '3 hours ago', status: 'success' },
];

export default function Dashboard() {
  return (
    <div className="dashboard-page" data-testid="dashboard-page">
      {/* Stats Grid */}
      <div className="stats-grid">
        {stats.map((stat, index) => (
          <div key={index} className={`stat-card ${stat.color}`} data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="stat-icon">
              <stat.icon size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-label">{stat.label}</span>
              <span className="stat-value">{stat.value}</span>
              <span className={`stat-change ${stat.trend}`}>
                {stat.trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {stat.change}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="charts-grid">
        <div className="chart-card large">
          <div className="chart-header">
            <h3>Revenue vs Cost</h3>
            <div className="chart-legend">
              <span className="legend-item revenue"><span className="dot"></span> Revenue</span>
              <span className="legend-item cost"><span className="dot"></span> Cost</span>
            </div>
          </div>
          <div className="chart-body">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#f43f5e" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `$${v/1000}k`} />
                <Tooltip 
                  contentStyle={{ 
                    background: '#1e293b', 
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`$${value.toLocaleString()}`, '']}
                />
                <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fillOpacity={1} fill="url(#colorRevenue)" />
                <Area type="monotone" dataKey="cost" stroke="#f43f5e" fillOpacity={1} fill="url(#colorCost)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>Courier Volume</h3>
            <span className="chart-subtitle">Bags vs Singles</span>
          </div>
          <div className="chart-body">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={courierData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" stroke="#64748b" fontSize={12} />
                <YAxis dataKey="name" type="category" stroke="#64748b" fontSize={12} width={100} />
                <Tooltip 
                  contentStyle={{ 
                    background: '#1e293b', 
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="bags" fill="#06b6d4" name="Bags" radius={[0, 4, 4, 0]} />
                <Bar dataKey="singles" fill="#84cc16" name="Singles" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent Activity & Quick Actions */}
      <div className="bottom-grid">
        <div className="activity-card">
          <div className="card-header">
            <h3>Recent Activity</h3>
            <button className="refresh-btn">
              <RefreshCw size={16} />
            </button>
          </div>
          <div className="activity-list">
            {recentActivity.map((item) => (
              <div key={item.id} className="activity-item">
                <div className={`activity-icon ${item.status}`}>
                  {item.status === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                </div>
                <div className="activity-content">
                  <p className="activity-message">{item.message}</p>
                  <span className="activity-time">
                    <Clock size={12} /> {item.time}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="quick-actions-card">
          <div className="card-header">
            <h3>Quick Actions</h3>
          </div>
          <div className="quick-actions-grid">
            <button className="quick-action-btn" data-testid="quick-sync-tasoko">
              <RefreshCw size={20} />
              <span>Sync Tasoko</span>
            </button>
            <button className="quick-action-btn" data-testid="quick-upload-manifest">
              <Package size={20} />
              <span>Upload Manifest</span>
            </button>
            <button className="quick-action-btn" data-testid="quick-create-invoice">
              <Receipt size={20} />
              <span>Create Invoice</span>
            </button>
            <button className="quick-action-btn" data-testid="quick-view-reports">
              <TrendingUp size={20} />
              <span>View Reports</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
